import axios from 'axios'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useReduxAuth} from '../../../auth'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import MetaTags from '../../../../meta/MetaTags'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import { USER_ROLES } from '../../../../constants/StatusConstants'
import CriticalitySettingsPage from '../criticality-settings/CriticalitySettingsPage'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

// ---- Type Definitions ----
export interface BPSetting {
  id: number
  bp_category: '1' | '2' | '3' | '4' | '5' | '6'
  enabled: boolean
}
export interface StaffMember {
  id: number
  first_name: string
  last_name: string
  email: string
  role_id: number
}
export interface NotificationRecipient {
  user: any
  id: number
  doctor_id: number
  recipient_email: string
}

// ---- Custom Hook ----
function useNotificationRecipients(doctorId: number | string, queryParams?: string | null) {
  const [recipients, setRecipients] = useState<NotificationRecipient[]>([])
  const [loading, setLoading] = useState(false)

  const fetchRecipients = async () => {
    setLoading(true)
    try {
      const resp = await axios.get<{data: {recipients: NotificationRecipient[]}}>(
        API_ENDPOINTS.GET_DOCTOR_NOTIFICATION_RECIPIENTS(doctorId, queryParams)
      )
      setRecipients(resp.data.data?.recipients ?? [])
    } catch (err) {
      toast.error('Failed to load alert recipients')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRecipients()
    // eslint-disable-next-line
  }, [doctorId, queryParams])

  const addRecipient = async (email: string) => {
    setLoading(true)
    try {
      await axios.post(API_ENDPOINTS.ADD_DOCTOR_NOTIFICATION_RECIPIENT(doctorId), {
        recipient_email: email,
      })
      toast.success('Recipient added')
      fetchRecipients()
    } catch (err: any) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.message || 'Failed to add recipient'
        : (err as Error).message
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const removeRecipient = async (email: string) => {
    setLoading(true)
    try {
      await axios.delete(API_ENDPOINTS.REMOVE_DOCTOR_NOTIFICATION_RECIPIENT(doctorId, email))
      toast.success('Recipient removed')
      fetchRecipients()
    } catch (err: any) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.message || 'Failed to remove recipient'
        : (err as Error).message
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return {recipients, addRecipient, removeRecipient, loading}
}

// ---- Recipient Manager Component ----
interface RecipientManagerProps {
  doctorId: number
  currentUserEmail: string
  staffList: StaffMember[]
  queryParams?: string | null
}
const NotificationRecipientManager: React.FC<RecipientManagerProps> = ({
  doctorId,
  currentUserEmail,
  staffList,
  queryParams,
}) => {
  const {recipients, addRecipient, removeRecipient, loading} = useNotificationRecipients(doctorId, queryParams)
  const [selectedEmail, setSelectedEmail] = useState<string>('')
  const [addingEmail, setAddingEmail] = useState<boolean>(false)
  const [removingEmail, setRemovingEmail] = useState<string | null>(null)

  // Get emails already in recipients
  const recipientEmails = recipients?.map((r) => r.user?.email) ?? []

  // Separate doctor and staff members from the staff list
  const doctor = staffList?.find((person) => person.role_id === USER_ROLES.DOCTOR)
  const actualStaff = staffList?.filter((person) => person.role_id !== USER_ROLES.DOCTOR) ?? []

  // Filter out already selected recipients
  const availableOptions = []
  
  // Add doctor if not already a recipient
  if (doctor && !recipientEmails.includes(doctor.email)) {
    const isCurrentUser = doctor.email === currentUserEmail
    availableOptions.push({
      label: `${isCurrentUser ? '(You)' : doctor.first_name + ' ' + (doctor.last_name || '')} — ${doctor.email}`,
      value: doctor.email,
    })
  }
  
  // Add staff members if not already recipients
  actualStaff.forEach((staff) => {
    if (!recipientEmails.includes(staff.email)) {
      const isCurrentUser = staff.email === currentUserEmail
      availableOptions.push({
        label: `${isCurrentUser ? '(You) ' : ''}${staff.first_name} ${staff.last_name || ''} — ${staff.email}`,
        value: staff.email,
      })
    }
  })

  const allOptions = availableOptions

  const handleAdd = async () => {
    if (selectedEmail) {
      setAddingEmail(true)
      try {
        await addRecipient(selectedEmail)
        setSelectedEmail('') // Reset dropdown after add
      } finally {
        setAddingEmail(false)
      }
    }
  }

  const handleRemove = async (email: string) => {
    setRemovingEmail(email)
    try {
      await removeRecipient(email)
    } finally {
      setRemovingEmail(null)
    }
  }

  return (
    <div className='mb-4'>
      <div className='d-flex justify-content-between mb-3 align-items-center'>
        <label htmlFor='recipientDropdown' className='form-label fw-medium'>
          Add recipient for alert emails:
        </label>
        <PrimaryButton
          onClick={handleAdd}
          disabled={!selectedEmail || loading || addingEmail}
          type='button'
        >
          {addingEmail ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
              Adding...
            </>
          ) : (
            'Add'
          )}
        </PrimaryButton>
      </div>
      <div className='input-group'>
        <select
          id='recipientDropdown'
          className='form-select'
          value={selectedEmail}
          onChange={(e) => setSelectedEmail(e.target.value)}
        >
          <option value=''>Select staff or self…</option>
          {allOptions?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className='mt-3'>
        <label className='form-label fw-medium'>Current Recipients:</label>
        <ul className='list-group'>
          {recipients?.map((r) => {
            // Find the person in staffList by email
            const person = staffList?.find((s) => s.email === r.user?.email)
            const isCurrentUser = r.user?.email === currentUserEmail
            
            let displayName = r.user?.email
            if (person) {
              if (person.role_id === USER_ROLES.DOCTOR) {
                displayName = `${isCurrentUser ? '(You)' : person.first_name + ' ' + (person.last_name || '')} — ${r.user?.email}`
              } else {
                displayName = `${isCurrentUser ? '(You) ' : ''}${person.first_name} ${person.last_name || ''} — ${r.user?.email}`
              }
            }
            
            return (
              <li
                key={r.user?.email}
                className='list-group-item d-flex justify-content-between align-items-center'
              >
                <span>{displayName}</span>
                <button
                  type='button'
                  className='btn btn-sm btn-danger'
                  onClick={() => handleRemove(r.user?.email)}
                  disabled={loading || removingEmail === r.user?.email}
                >
                  {removingEmail === r.user?.email ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-1' aria-hidden='true'></span>
                      Removing...
                    </>
                  ) : (
                    'Remove'
                  )}
                </button>
              </li>
            )
          })}
          {(recipients?.length ?? 0) === 0 && (
            <li className='list-group-item text-muted'>No recipients selected</li>
          )}
        </ul>
      </div>
    </div>
  )
}

// ---- Main Page Component ----
const ALL_CATEGORIES: BPSetting['bp_category'][] = ['1', '2', '3', '4', '5', '6']
const CATEGORY_LABELS: Record<BPSetting['bp_category'], {label: string; color: string}> = {
  '1': {label: 'Low', color: 'bp-low'},
  '2': {label: 'Normal', color: 'bg-normal'},
  '3': {label: 'Elevated', color: 'bg-elevated'},
  '4': {label: 'Stage 1', color: 'bp-stage1'},
  '5': {label: 'Stage 2', color: 'bp-stage2'},
  '6': {label: 'Crisis', color: 'bg-critical'},
}

const BPAlertSettings: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const isDoctor = currentUser.role_id === USER_ROLES.DOCTOR
  const isMA = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT
  
  // For MA, use selected doctor ID; for doctors, use their own ID; for others, use doctor_id
  const doctorId = isMA && maSelectedDoctorId
    ? maSelectedDoctorId
    : isDoctor
    ? currentUser.id
    : currentUser.doctor_id

  const [settings, setSettings] = useState<BPSetting[] | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const [staffList, setStaffList] = useState<StaffMember[]>([])
  const [showHealthAlertDialog, setShowHealthAlertDialog] = useState(false)
  
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])

  // Fetch all doctors for MA (MA can access all doctors via dropdown)
  useEffect(() => {
    if (isMA) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            maDropdown.setSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          console.error('Error fetching doctors for MA:', err)
          setMaDoctors([])
        })
    }
  }, [isMA, maSelectedDoctorId, maDropdown])

  // Clear settings immediately when doctor selection changes (for MA)
  useEffect(() => {
    if (isMA && maSelectedDoctorId) {
      // Clear settings immediately when doctor changes to show loading state
      setSettings(null)
      setLoading(true)
    }
  }, [maSelectedDoctorId, isMA])

  // Fetch doctor-level settings on mount and when doctor selection changes
  useEffect(() => {
    // Skip if MA and no doctor selected
    if (isMA && !maSelectedDoctorId) {
      setSettings([])
      setLoading(false)
      return
    }

    if (!doctorId) {
      setSettings([])
      setLoading(false)
      return
    }

    const fetchSettings = async () => {
      setLoading(true)
      
      try {
        // Route parameter (doctorId) should dynamically change based on selected doctor
        // No query params needed - the route doctorId is sufficient
        const resp = await axios.get(
          API_ENDPOINTS.GET_BP_ALERT_SETTINGS(doctorId)
        )
        
        // Handle different response structures
        const settingsData = resp.data?.data?.settings ?? 
                            resp.data?.settings ?? 
                            resp.data?.data ?? 
                            null
        setSettings(settingsData)
      } catch (err) {
        const msg = axios.isAxiosError(err)
          ? err.response?.data?.message || 'Failed to load settings'
          : (err as Error).message
        toast.error(msg)
        setSettings([]) // Set empty array on error
      } finally {
        setLoading(false)
      }
    }
    fetchSettings()
  }, [doctorId, isMA, maSelectedDoctorId])

  // Fetch staff for dropdown
  useEffect(() => {
    // Skip if MA and no doctor selected
    if (isMA && !maSelectedDoctorId) {
      setStaffList([])
      return
    }

    const fetchStaff = async () => {
      try {
        // Build query params for MA
        const queryParams = isMA && maSelectedDoctorId
          ? new URLSearchParams({
              doctor_id: maSelectedDoctorId.toString(),
            }).toString()
          : null

        const resp = await axios.get<{staff: StaffMember[]}>(
          API_ENDPOINTS.GET_DOCTOR_ACTIVE_STAFF(doctorId, queryParams)
        )
        setStaffList(resp.data?.staff ?? [])
      } catch (err) {
        const msg = axios.isAxiosError(err)
          ? err.response?.data?.message || 'Failed to load staff list'
          : (err as Error).message
        toast.error(msg)
      }
    }
    if (doctorId) {
      fetchStaff()
    }
  }, [doctorId, isMA, maSelectedDoctorId])

  const toggleCategory = (category: BPSetting['bp_category']) => {
    if (!settings) return
    setSettings((prev) =>
      prev!?.map((s) => (s.bp_category === category ? {...s, enabled: !s.enabled} : s))
    )
  }

  const handleSave = async () => {
    if (!settings) return
    
    setSaveLoading(true)
    try {
      await axios.put(API_ENDPOINTS.UPDATE_BP_ALERT_SETTINGS(doctorId), {
        settings: settings?.map(({bp_category, enabled}) => ({bp_category, enabled})) ?? [],
      })
      toast.success('BP alert settings saved')
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.message || 'Failed to save'
        : (err as Error).message
      toast.error(msg)
    } finally {
      setSaveLoading(false)
    }
  }

  const handleInitialize = async () => {
    setLoading(true)
    try {
      await axios.put(API_ENDPOINTS.UPDATE_BP_ALERT_SETTINGS(doctorId), {
        settings: ALL_CATEGORIES?.map((bp_category) => ({bp_category, enabled: true})) ?? [],
      })
      // Route parameter (doctorId) should dynamically change based on selected doctor
      const resp = await axios.get<{data: {settings: BPSetting[]}}>(
        API_ENDPOINTS.GET_BP_ALERT_SETTINGS(doctorId)
      )
      setSettings(resp.data?.data?.settings ?? null)
      toast.success('All alerts enabled; you can now fine-tune them')
    } catch {
      toast.error('Could not initialize settings')
    } finally {
      setLoading(false)
    }
  }


  // Render loading
  if (settings === null && (!isMA || maSelectedDoctorId)) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  const isEmpty = settings?.length === 0

  // Build query params for MA
  const queryParams = isMA && maSelectedDoctorId
    ? new URLSearchParams({
        doctor_id: maSelectedDoctorId.toString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
      }).toString()
    : new URLSearchParams({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
      }).toString()

  return (
    <>
      <MetaTags title='Blood Pressure Alert Settings' />
      <div className='container-lg py-4'>
        {/* Doctor Dropdown for MA */}
        {isMA && (
          <div className='d-flex justify-content-end mb-3'>
            <div className='w-25'>
              <label htmlFor='doctorSelect' className='form-label'>
                Select Doctor
              </label>
              <select
                id='doctorSelect'
                className='form-select'
                value={maSelectedDoctorId ?? ''}
                onChange={(e) => {
                  const val = e.target.value
                  maDropdown.setSelectedDoctorId(val ? Number(val) : undefined)
                }}
                disabled={maDoctors.length === 0}
              >
              {maDoctors.length === 0 ? (
                <option value=''>-- No doctors available --</option>
              ) : (
                <>
                  <option value=''>-- Select Doctor --</option>
                  {maDoctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.full_name}
                    </option>
                  ))}
                </>
              )}
              </select>
            </div>
          </div>
        )}

        <div className='d-flex justify-content-start align-items-center gap-2 mb-4'>
          <h1 className='mb-0'>Notification Settings</h1>
          <i
            className="bi bi-info-circle fs-3 text-primary mt-2"
            role="button"
            title="See the default health alerts here"
            onClick={()=>setShowHealthAlertDialog(true)}
          ></i>
        </div>

        {/* Recipient management */}
        {(!isMA || maSelectedDoctorId) && (
          <NotificationRecipientManager
            doctorId={doctorId}
            currentUserEmail={currentUser.email}
            staffList={staffList}
            queryParams={queryParams}
          />
        )}

        {/* Show message if MA hasn't selected a doctor */}
        {isMA && !maSelectedDoctorId && (
          <div className='alert alert-info' role='alert'>
            Please select a doctor to view and manage notification settings.
          </div>
        )}

        {(!isMA || maSelectedDoctorId) && isEmpty ? (
          <div className='text-center py-5'>
            <p className='mb-4'>You haven't set up any alert categories yet.</p>
            <button
              className='btn btn-lg btn-primary'
              onClick={handleInitialize}
              disabled={loading}
            >
              {loading ? 'Initializing…' : 'Set Alerts'}
            </button>
          </div>
        ) : (!isMA || maSelectedDoctorId) ? (
          <div className='row g-4'>
            <div className='col-12'>
              <div className='card shadow rounded-5'>
                <div className='card-body'>
                  <div className='list-group list-group-flush'>
                    {settings
                      ?.filter((s) => s.bp_category !== '2') /* remove "Normal" */
                      ?.map((s) => (
                        <div key={s.bp_category} className='list-group-item py-3'>
                          <div className='d-flex justify-content-between align-items-center'>
                            <div className='d-flex align-items-center'>
                              <div
                                className={`${
                                  CATEGORY_LABELS[s.bp_category].color
                                } rounded-circle me-3`}
                                style={{width: '1rem', height: '1rem'}}
                              />
                              <span className='fw-medium'>
                                {CATEGORY_LABELS[s.bp_category].label}
                              </span>
                            </div>
                            <div className='form-check form-switch m-0'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                checked={s.enabled}
                                onChange={() => toggleCategory(s.bp_category)}
                                style={{width: '3em', height: '1.5em'}}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className='py-3'>
                <div className='d-flex justify-content-end'>
                  <button
                    className='btn btn-primary px-6 rounded-3 py-3'
                    onClick={handleSave}
                    disabled={loading || saveLoading}
                  >
                    {saveLoading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
                        Saving Changes...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Health Alerts - Default Settings Dialog */}
      {showHealthAlertDialog && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowHealthAlertDialog(false)}
                ></button>
              </div>

              <div className="modal-body">
                <CriticalitySettingsPage />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default BPAlertSettings
